import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import moment from "moment";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { SocialPostNotification } from "../../../cores/notifications/interfaces/api/get-notifications";
import { stripSocialPostPreviewPlaceholders } from "../../../cores/notifications/utils/socialPostNotificationNormalize";
import { SocialProviderNameMapper } from "../../../cores/socialPost/config";
import { SocialProviderIcon } from "../socialPost/selector/SocialProviderIcon";
import "./SocialPostNotificationItem.scss";

interface Props {
  notification: SocialPostNotification;
  /** Navbar dropdown: layout más compacto */
  compact?: boolean;
  isDarkMode?: boolean;
  /** Página: la franja superior ya muestra fecha */
  suppressFooterTime?: boolean;
}

function normalizeProviderId(social: string): string {
  const raw = (social || "").trim();
  if (!raw) return "facebook";
  const lower = raw.toLowerCase().replace(/\s+/g, "");
  const map: Record<string, string> = {
    instagram: "instagram",
    facebook: "facebook",
    twitter: "twitter",
    linkedin: "linkedIn",
    linkedinbusiness: "linkedInBusiness",
    tiktok: "tiktok",
  };
  return map[lower] ?? raw;
}

function networkDisplayName(social: string): string {
  const id = normalizeProviderId(social);
  const mapped =
    SocialProviderNameMapper[id] ||
    SocialProviderNameMapper[id.toLowerCase()];
  if (mapped) return mapped;
  return social.charAt(0).toUpperCase() + social.slice(1);
}

export default function SocialPostNotificationItem({
  notification,
  compact = false,
  isDarkMode = false,
  suppressFooterTime = false,
}: Props) {
  const intl = useIntl();
  const providerId = normalizeProviderId(notification.social);
  const titleNetwork = networkDisplayName(notification.social);

  const previewText = useMemo(() => {
    const t = stripSocialPostPreviewPlaceholders(
      (notification.content || "").trim()
    );
    return t;
  }, [notification.content]);

  const postedLabel = useMemo(() => {
    const raw = notification.postedAt;
    if (!raw) return null;
    const m = moment(raw);
    if (!m.isValid()) return null;
    return m.calendar(undefined, {
      sameDay: `[${intl.formatMessage({ id: "app.notifications.timeToday" })}] HH:mm`,
      lastDay: `[${intl.formatMessage({ id: "app.notifications.timeYesterday" })}] HH:mm`,
      lastWeek: "DD/MM HH:mm",
      sameElse: "DD/MM/YYYY HH:mm",
    });
  }, [notification.postedAt, intl]);

  const ok = notification.status === 1;

  return (
    <div
      className={cn("social-post-notification", {
        "social-post-notification--compact": compact,
        "social-post-notification--dark": isDarkMode,
      })}
    >
      <div className="social-post-notification__thumb" aria-hidden>
        {notification.imageUrl ? (
          <img src={notification.imageUrl} alt="" />
        ) : (
          <div className="social-post-notification__thumb-fallback">
            <SocialProviderIcon
              className="m-0"
              isSelected
              showErrors={false}
              socialProvider={{ id: providerId }}
            />
          </div>
        )}
      </div>
      <div className="social-post-notification__body">
        <p className="social-post-notification__title">
          {intl.formatMessage(
            { id: "app.notifications.socialPostTitle" },
            { network: titleNetwork }
          )}
        </p>
        {notification.accountName ? (
          <p className="social-post-notification__account">
            {notification.accountName}
          </p>
        ) : null}
        {previewText ? (
          <p className="social-post-notification__preview">{previewText}</p>
        ) : notification.hasMedia || notification.imageUrl ? (
          <p className="social-post-notification__preview text-muted font-italic">
            {intl.formatMessage({
              id: "app.notifications.socialPostNoCaptionWithMedia",
            })}
          </p>
        ) : null}
        <div className="social-post-notification__footer">
          <span
            className={cn("social-post-notification__status", {
              "social-post-notification__status--ok": ok,
              "social-post-notification__status--err": !ok,
            })}
          >
            <FontAwesomeIcon
              icon={["fas", ok ? "check" : "times"]}
              className="flex-shrink-0"
            />
            {ok
              ? intl.formatMessage({ id: "app.socialMediaPost.postedCorecctly" })
              : intl.formatMessage({ id: "app.socialMediaPost.postedError" })}
          </span>
          {postedLabel && !suppressFooterTime ? (
            <span className="social-post-notification__time">{postedLabel}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
